import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { LangService } from '@reforgium/presentia';
import { AppNotificationService } from 'services/app-notification.service';

import { AuthService } from 'use-cases/auth.service';

import { parseApiError } from './api-error.util';

const AUTH_RETRY_CONTEXT = new HttpContextToken<boolean>(() => false);
const AUTH_STATUSES = new Set([401, 403]);
const SESSION_EXPIRED_MESSAGE = 'Сессия истекла. Пожалуйста, авторизуйтесь снова.';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const langService = inject(LangService);
  const notificationsService = inject(AppNotificationService);

  const excludedUrls: string[] = ['/auth/sign-in', '/auth/refresh'];
  const headers = { 'Accept-Language': langService.currentLang() };
  const withoutCredentials = excludedUrls.some((url) => req.url.endsWith(url) || req.url.includes(url + '/'));

  const showError = (error: HttpErrorResponse) => {
    const message =
      parseApiError(error, { lang: langService.currentLang(), includeFields: true }) ??
      error.message ??
      error.statusText;

    notificationsService.error(message);
  };

  const expireSession = () => {
    if (authService.expire()) {
      notificationsService.error(SESSION_EXPIRED_MESSAGE);
    }
  };

  const retryWithCurrentToken = () => {
    const token = authService.token;

    if (!token) {
      expireSession();

      return throwError(() => new Error('Access token is unavailable after refresh'));
    }

    const retriedRequest = req.clone({
      context: req.context.set(AUTH_RETRY_CONTEXT, true),
      setHeaders: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
      params: cleanHttpParams(req.params),
    });

    return next(retriedRequest).pipe(
      catchError((retryError: HttpErrorResponse) => {
        if (AUTH_STATUSES.has(retryError.status)) {
          expireSession();
        } else {
          showError(retryError);
        }

        return throwError(() => retryError);
      }),
    );
  };

  const handleAuthCatch = (error: HttpErrorResponse) => {
    if (withoutCredentials) {
      return throwError(() => error);
    }

    if (!AUTH_STATUSES.has(error.status)) {
      showError(error);

      return throwError(() => error);
    }

    const alreadyRetried = req.context.get(AUTH_RETRY_CONTEXT);

    if (alreadyRetried || !authService.refreshToken) {
      expireSession();

      return throwError(() => error);
    }

    return authService.refresh().pipe(
      catchError((refreshError: HttpErrorResponse) => {
        expireSession();

        return throwError(() => refreshError);
      }),
      switchMap(retryWithCurrentToken),
    );
  };

  if (withoutCredentials) {
    return next(req).pipe(catchError(handleAuthCatch));
  }

  const params = cleanHttpParams(req.params);
  const token = authService.token;
  const request = token
    ? req.clone({
        setHeaders: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
        params,
      })
    : req.clone({ setHeaders: headers, params });

  return next(request).pipe(catchError(handleAuthCatch));
};

function cleanHttpParams(params: HttpParams): HttpParams {
  let result = new HttpParams();

  params.keys().forEach((key) => {
    const value = params.get(key);

    if (value !== 'null' && value !== 'undefined' && value?.length) {
      result = result.set(key, value);
    }
  });

  return result;
}
